import { CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router';
import HeroSection from './HeroSection';
import Layout from './Layout';
import { Button } from './ui/button';

const APPLY_URL = '/offers/cybersecurity-awareness-month-2026/apply';
const APPLY_CTA = 'Apply for Your $1,000 Credit';

const USES = [
  {
    number: '01',
    title: 'Team Engagement',
    description:
      "Run the simulations as a team competition. Developers solve vulnerabilities against each other, and a leaderboard shows who's ahead.",
    benefit: 'Getting your whole team involved.',
  },
  {
    number: '02',
    title: 'Assessment',
    description:
      'Give your developers a set of realistic scenarios and see how they handle each one. You find out where the gaps are before an attacker does.',
    benefit: "Seeing where your developers' gaps are.",
  },
  {
    number: '03',
    title: 'Training',
    description:
      "Developers work through code that contains real vulnerabilities and fix it themselves. It's hands-on practice instead of a lecture.",
    benefit: 'Building secure coding habits.',
  },
];

const KEY_POINTS = [
  '$1,000 in platform credit, at no cost to your organization.',
  'Secure coding simulations based on real vulnerabilities.',
  'Use them for a competition, an assessment, or training.',
  "One month of access after you're approved.",
];

const STEPS = [
  {
    number: '01',
    title: 'Apply',
    description: 'Fill in the form before October 31.',
  },
  {
    number: '02',
    title: 'Review',
    description: "We review your application and let you know if you're approved.",
  },
  {
    number: '03',
    title: 'Start Training',
    description:
      'Your credit is valid for one month, so you can run the simulations with your developers.',
  },
];

export default function CybersecurityAwarenessMonthPage() {
  return (
    <div>
      <Layout>
        <div className="space-y-32 pb-20">
          {/* Hero Section */}
          <div>
            <HeroSection
              title="Get $1,000 to Train Your Developers on Real Vulnerabilities"
              description="This October, Simulations Labs is giving organizations $1,000 in credit. Your developers practice finding and fixing real vulnerabilities, and you choose how to run it: as a team activity, an assessment, or training."
              subDescription="Applications close October 31."
              image="https://images.simulationslabs.com/website+articles/security+awareness+offer.png"
              blueTitle="Cybersecurity Awareness Month"
              imageContainerClassName="w-full md:w-1/2"
              imageClassName="mx-auto h-auto max-h-[360px] w-auto object-contain"
              buttons={[
                {
                  text: APPLY_CTA,
                  link: APPLY_URL,
                  variant: 'default',
                },
              ]}
            />
          </div>

          {/* Three Ways to Use Your Credit */}
          <div className="grid space-y-10">
            <div className="text-center">
              <h1 className="text-[42px] font-medium">Three Ways to Use Your Credit</h1>
              <p className="text-lighterBlack mx-auto mt-4 max-w-3xl text-lg">
                Pick what your team needs most.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {USES.map((use) => (
                <div
                  key={use.title}
                  className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-8"
                >
                  <span className="bg-lightBlue/10 text-lightBlue flex h-11 w-11 items-center justify-center rounded-full text-[15px] font-bold">
                    {use.number}
                  </span>
                  <h2 className="text-[20px] font-medium">{use.title}</h2>
                  <p className="flex-1 text-gray-500">{use.description}</p>
                  <div className="bg-lightBlue/10 border-lightBlue rounded-md border-l-4 px-4 py-3">
                    <span className="text-lightBlue text-[12px] font-bold tracking-wide uppercase">
                      Good for
                    </span>
                    <p className="text-[15px] font-medium">{use.benefit}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* What Your Developers Will Do */}
          <div className="flex flex-col items-center justify-between gap-10 md:flex-row">
            <div className="flex flex-col gap-4 md:w-1/2">
              <h1 className="text-[42px] leading-tight font-medium">
                What Your Developers Will Do
              </h1>
              <p className="text-lighterBlack text-lg">
                Every simulation puts them in front of vulnerable code. They find the problem, fix
                it, and see why it matters.
              </p>
              <ul className="mt-4 space-y-4">
                {KEY_POINTS.map((point) => (
                  <li key={point} className="flex gap-3">
                    <CheckCircle2 className="text-lightBlue mt-0.5 shrink-0" size={22} />
                    <span className="text-lighterBlack text-[17px]">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex w-full items-center justify-center md:w-1/2">
              <img
                src="https://images.simulationslabs.com/website+articles/Secure+Code+Comparison+Panel.png"
                alt="Vulnerable code compared side by side with its secure fix"
                className="h-auto w-full object-contain"
              />
            </div>
          </div>

          {/* How It Works */}
          <div className="grid space-y-10">
            <div className="text-center">
              <h1 className="text-[42px] font-medium">How It Works</h1>
              <p className="text-lighterBlack mx-auto mt-4 max-w-3xl text-lg">
                For organizations with developers. CTOs and security team leads apply on behalf of
                their teams.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {STEPS.map((step) => (
                <div
                  key={step.number}
                  className="bg-gray border-lightBlue space-y-3 rounded-lg border-t-4 p-8"
                >
                  <h1 className="text-4xl font-light text-blue-400 opacity-50">{step.number}</h1>
                  <h2 className="text-[20px] font-medium">{step.title}</h2>
                  <p className="text-gray-500">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="flex justify-center">
            <div className="banner-bg flex w-full max-w-[1152px] flex-col items-center justify-center overflow-hidden rounded-xl px-10 py-16">
              <h2 className="mb-4 max-w-[720px] text-center text-[42px] leading-[1.15] font-extrabold text-white">
                Give your developers $1,000 of secure coding practice
              </h2>
              <p className="mb-10 max-w-[600px] text-center text-lg text-white/90">
                Credits are for approved applicants. Applications close October 31.
              </p>
              <Button asChild className="w-fit">
                <Link to={APPLY_URL}>{APPLY_CTA}</Link>
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    </div>
  );
}
